import { createSelector } from 'redux-bundler';
import { snakeCase } from 'lodash';
import matchSorter from 'match-sorter';

import SsmParameter from '../models/ssm_parameter';
import SsmParameterSync from '../models/ssm_parameter_sync';

export default {
  name: 'ssmParameters',
  getReducer: () => {
    const initialData = {
      data: null,
      loading: false,
      loaded: false,
      sortDirection: 'ASC',
      sortBy: 'name',
      lastSuccess: null,
      activeParameterId: null,
      query: '',
    };

    return (state = initialData, { type, payload }) => {
      if (type === 'FETCH_SSM_PARAMETERS_START') {
        return {
          ...state,
          loading: true,
          loaded: false,
        };
      }
      if (type === 'FETCH_SSM_PARAMETERS_SUCCESS') {
        return {
          ...state,
          loading: false,
          loaded: true,
          data: payload.result,
          lastSuccess: payload.time,
        };
      }
      if (type === 'CHANGE_SSM_PARAMETERS_SORT') {
        return {
          ...state,
          sortDirection: payload.sortDirection,
          sortBy: payload.sortBy,
        };
      }
      if (type === 'RESET_ACTIVE_FACILITY') {
        return {
          ...state,
          data: null,
          loaded: false,
        };
      }
      if (type === 'RESET_SSM_PARAMETERS') {
        return {
          ...state,
          data: null,
          loaded: false,
        };
      }
      if (type === 'SET_ACTIVE_SSM_PARAMETER') {
        return {
          ...state,
          activeParameterId: payload.parameterId,
        };
      }
      if (type === 'CHANGE_SSM_PARAMETER_QUERY') {
        return {
          ...state,
          query: payload.query,
        };
      }

      return state;
    };
  },

  doFetchSsmParameters: () => async ({ dispatch, getState, store }) => {
    dispatch({ type: 'FETCH_SSM_PARAMETERS_START' });

    const state = getState();
    const facility = store.selectActiveFacility(state);
    const { sortBy, sortDirection } = state.ssmParameters;

    if (!facility) {
      dispatch({
        type: 'FETCH_SSM_PARAMETERS_SUCCESS',
        payload: { result: [], time: Date.now() },
      });
      return;
    }

    try {
      const response = await SsmParameter
        .where({
          facility_id: facility.id,
        })
        .order({
          [snakeCase(sortBy)]: sortDirection.toLowerCase(),
        })
        .includes(['facility'])
        .all();

      dispatch({
        type: 'FETCH_SSM_PARAMETERS_SUCCESS',
        payload: { result: response.data, time: Date.now() },
      });
    } catch (error) {
      console.error('Failed to fetch SSM parameters:', error);
      dispatch({
        type: 'FETCH_SSM_PARAMETERS_SUCCESS',
        payload: { result: [], time: Date.now() },
      });
    }
  },

  doCreateSsmParameter: (values) => async ({ dispatch, getState, store }) => {
    const state = getState();
    const facility = store.selectActiveFacility(state);

    // Create the parameter instance
    const ssmParameter = new SsmParameter({
      name: values.name,
      parameterType: values.parameterType,
      value: values.value,
      description: values.description,
      kmsKeyId: values.kmsKeyId,
      facility: facility,
    });

    // Just try to save it
    await ssmParameter.save({
      with: ['facility.id']
    });

    // Refresh the list
    await store.doFetchSsmParameters();

    return ssmParameter;
  },

  doUpdateSsmParameter: (values) => async ({ getState, store }) => {
    const state = getState();
    const activeParameter = store.selectActiveSsmParameter(state);

    if (!activeParameter) {
      throw new Error('No active SSM parameter selected');
    }

    // Create a copy to avoid mutating the original
    const parameter = activeParameter.dup();

    // Update the parameter with new values using assignAttributes
    parameter.assignAttributes(values);

    // Save the changes
    const success = await parameter.save({
      with: ['facility.id']
    });

    if (success) {
      // Refresh the list
      await store.doFetchSsmParameters();
    }

    return parameter;
  },

  doDeleteSsmParameter: () => async ({ getState, store }) => {
    const state = getState();
    const activeParameter = store.selectActiveSsmParameter(state);

    if (!activeParameter) {
      throw new Error('No active SSM parameter selected');
    }

    // Delete the parameter
    await activeParameter.destroy();

    // Refresh the list
    await store.doFetchSsmParameters();

    return activeParameter;
  },

  doRefreshSsmParameters: () => async ({ getState, store }) => {
    const state = getState();
    const facility = store.selectActiveFacility(state);

    if (!facility) {
      throw new Error('No active facility');
    }

    // Create a sync resource to trigger the refresh
    const sync = new SsmParameterSync({
      facilityId: facility.id,
    });

    // Trigger the sync on the backend
    await sync.save();

    // Refresh the parameters list
    await store.doFetchSsmParameters();
  },

  doChangeSsmParametersSort: ({ sortDirection, sortBy }) => async ({
    dispatch,
    store,
  }) => {
    dispatch({
      type: 'CHANGE_SSM_PARAMETERS_SORT',
      payload: { sortDirection, sortBy },
    });
    store.doFetchSsmParameters();
  },

  doResetSsmParameters: () => ({ dispatch }) => {
    dispatch({ type: 'RESET_SSM_PARAMETERS' });
  },

  doSetActiveSsmParameter: (parameterId) => ({ dispatch }) => {
    dispatch({ type: 'SET_ACTIVE_SSM_PARAMETER', payload: { parameterId } });
  },

  doChangeSsmParameterQuery: (query) => ({ dispatch }) => {
    dispatch({ type: 'CHANGE_SSM_PARAMETER_QUERY', payload: { query } });
  },

  selectSsmParameters: state => state.ssmParameters.data,
  selectSsmParameterQuery: state => state.ssmParameters.query,
  selectSsmParametersLoading: state => state.ssmParameters.loading,
  selectSsmParametersLoaded: state => state.ssmParameters.loaded,
  selectSsmParametersSortBy: state => state.ssmParameters.sortBy,
  selectSsmParametersSortDirection: state => state.ssmParameters.sortDirection,
  selectActiveSsmParameterId: state => state.ssmParameters.activeParameterId,

  selectActiveSsmParameter: createSelector(
    'selectSsmParameters',
    'selectActiveSsmParameterId',
    (ssmParameters, activeParameterId) => {
      if (!ssmParameters || !activeParameterId) {
        return null;
      }
      return ssmParameters.find(param => param.id === activeParameterId);
    }
  ),

  selectFilteredSsmParameters: createSelector(
    'selectSsmParameters',
    'selectSsmParameterQuery',
    (ssmParameters, query) => {
      if (!ssmParameters) {
        return null;
      }

      if (!query) {
        return ssmParameters;
      }

      return matchSorter(ssmParameters, query, {
        keys: [
          'name', // Always searchable
          (item) => {
            // Only search values for unencrypted parameters
            // SecureString values are never searchable per security requirements
            if (item.parameterType === 'String' || item.parameterType === 'StringList') {
              return item.value || '';
            }
            return '';
          }
        ]
      });
    }
  ),

  reactShouldFetchSsmParameters: createSelector(
    'selectRouteApis',
    'selectSsmParametersLoading',
    'selectSsmParametersLoaded',
    'selectActiveFacility',
    (apis, loading, loaded, activeFacility) => {
      if (!activeFacility) {
        return null;
      }
      if (!apis.includes('ssm_parameters')) {
        return null;
      }
      if (loading) {
        return null;
      }
      if (loaded) {
        return null;
      }
      return {
        actionCreator: 'doFetchSsmParameters',
      };
    },
  ),
};